import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { WallMassTable, WallTable } from "building-types/walls/wallTableTypes";
import type { WallLayer } from "building-types/walls/wallTypes";
import {
  type WallMass,
  WallLOD,
  type Wall,
  type SolidWall,
  type SolidWallMass,
  type VoidWall,
} from "building-types/walls/wallTypes";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";

/**
 * wallLayers returns the layers of a WallMass of a specific WallLayerType and WallLOD
 */
export function wallMassLayers(wallMass: WallMass, wallLayer: WallLayerType | undefined, wallLOD: WallLOD | undefined) {
  if (wallLayer === WallLayerType.MASS) {
    return [wallMass];
  }
  const layers = wallMass.layers.filter((l) => l.wallLayerType === wallLayer);
  if (!layers.length || wallLOD === WallLOD.L1) {
    return layers;
  }
  return layers.reduce((ls, l) => {
    ls.push(...wallMassLayers(l, wallLayer, WallLOD.L1));
    return ls;
  }, [] as WallMass[]);
}

/**
 * wallMassCrossSectionLayers returns the @type { WallMass[] } of a wall
 * cut at a plane relative to the @type { WallMass["floorElevation"] }.
 *
 * @param wallLOD the level of detail required for this cut
 * @param cutPlane the height offset from floorElevation to cut this wall
 * @param wallLayer the type of layer to be cut. Layers can be nested so this will only return one.
 */
export function wallMassCrossSectionLayers(
  wallMass: WallMass,
  wallLayer: WallLayerType | undefined,
  wallLOD = WallLOD.L1,
  cutPlane = FLOOR_PLAN_CUT_PLANE,
) {
  return (
    wallMassLayers(wallMass, wallLayer, wallLOD).filter(
      ({ section }) => section.bottomOffset <= cutPlane && section.topOffset > cutPlane,
    ) ?? false // this is explicit until strict null checks are enabled
  );
}

/**
 * wallMassCrossSectionLayer returns the @type { WallMass } of a wall
 * cut at a plane relative to the @type { WallMass["floorElevation"] }.
 *
 * solidWallCrossSectionLayer will return @type { false } if no
 * layer is detected at the cutPlane.
 *
 * If you need a default layer returned, use wallLayerWithFallback instead.
 *
 * @param wallLOD the level of detail required for this cut
 * @param cutPlane the height offset from floorElevation to cut this wall
 * @param wallLayer the type of layer to be cut. Layers can be nested so this will only return one.
 * @deprecated use wallMassCrossSectionLayers instead - there may be more than one layer returned
 */
export function wallMassCrossSectionLayer(
  wallMass: WallMass,
  wallLayer: WallLayerType | undefined,
  wallLOD = WallLOD.L1,
  cutPlane = FLOOR_PLAN_CUT_PLANE,
) {
  return (
    wallMassLayers(wallMass, wallLayer, wallLOD).find(
      ({ section }) => section.bottomOffset <= cutPlane && section.topOffset > cutPlane,
    ) ?? false // this is explicit until strict null checks are enabled
  );
}

/**
 * wallCrossSectionLayers returns the @type { WallMass[] } of a wall
 * cut at a plane relative to the @type { Wall["floorElevation"] }.
 *
 * @param wallLOD the level of detail required for this cut
 * @param cutPlane the height offset from floorElevation to cut this wall
 * @param wallLayer the type of layer to be cut. Layers can be nested so this will only return one.
 */
export function wallCrossSectionLayers(wall: Wall, wallLayer: WallLayerType, wallLOD?: WallLOD, cutPlane?: number) {
  return wallMassCrossSectionLayers(wall.wallType === WallType.SOLID ? wall.mass : wall, wallLayer, wallLOD, cutPlane);
}

/**
 * wallCrossSectionLayer returns the @type { WallMass } of a wall
 * cut at a plane relative to the @type { Wall["floorElevation"] }.
 *
 * solidWallCrossSectionLayer will return @type { false } if no
 * layer is detected at the cutPlane.
 *
 * If you need a default layer returned, use wallLayerWithFallback instead.
 *
 * @param wallLOD the level of detail required for this cut
 * @param cutPlane the height offset from floorElevation to cut this wall
 * @param wallLayer the type of layer to be cut. Layers can be nested so this will only return one.
 * @deprecated use wallCrossSectionLayers instead - there may be more than one layer returned
 */
export function wallCrossSectionLayer(wall: Wall, wallLayer: WallLayerType, wallLOD?: WallLOD, cutPlane?: number) {
  return wallMassCrossSectionLayer(wall.wallType === WallType.SOLID ? wall.mass : wall, wallLayer, wallLOD, cutPlane);
}

/**
 * wallLayerWithFallback will always return a @type { WallMass }.
 *
 * The default cutPlane will return the WallMass for the WallSection located at the FLOOR_PLAN_CUT_PLANE
 * above the floorElevation that the wall is located on. There may be sections above or below it.
 * In the event that no layer is found, the WallMass is returned as a fallback.
 * @deprecated use the wallLOD property and do not use WallMass as a true representation of a wall
 */
export function wallLayerWithFallback({
  wall,
  wallLayer,
  wallLOD,
  cutPlane,
}: {
  wall: Wall;
  wallLayer: WallLayerType | undefined;
  wallLOD?: WallLOD;
  cutPlane?: number;
}) {
  switch (wall.wallType) {
    case WallType.VOID: {
      return voidWallLayerWithFallback(wall, wallLayer, wallLOD, cutPlane);
    }
    case WallType.SOLID:
    case WallType.SOLID_MASS: {
      return solidWallLayerWithFallback(wall, wallLayer, wallLOD, cutPlane);
    }
    default: {
      assertUnreachable(wall);
    }
  }
}

/**
 * The default cutPlane will return the SolidWallMass for the WallSection located at the FLOOR_PLAN_CUT_PLANE
 * above the floorElevation that the wall is located on. There may be sections above or below it.
 * In the event that no layer is found, the SolidWallMass is returned as a fallback.
 */
export function solidWallLayerWithFallback(
  solidWall: SolidWall | SolidWallMass,
  wallLayer: WallLayerType | undefined,
  wallLOD?: WallLOD,
  cutPlane?: number,
): SolidWallMass {
  const solidWallMass = solidWall.wallType === WallType.SOLID ? solidWall.mass : solidWall;
  const layer = wallMassCrossSectionLayer(solidWallMass, wallLayer, wallLOD, cutPlane);
  if (!layer || layer.wallType !== WallType.SOLID_MASS) {
    return solidWallMass;
  }
  return layer;
}

/**
 * @TODO remove usage and replace with solidWallLayer - this is not explicit about fallbacks
 * @deprecated use solidWallLayerWithFallback
 */
export function solidWallSubstrateLayer(solidWall: SolidWall | SolidWallMass, wallLOD?: WallLOD, cutPlane?: number) {
  return solidWallLayerWithFallback(solidWall, WallLayerType.SUBSTRATE, wallLOD, cutPlane);
}

/**
 * @TODO remove usage and replace with solidWallLayer - this is not explicit about fallbacks
 * @deprecated use solidWallLayerWithFallback
 */
export function solidWallFramingLayer(solidWall: SolidWall | SolidWallMass, wallLOD?: WallLOD, cutPlane?: number) {
  return solidWallLayerWithFallback(solidWall, WallLayerType.STRUCTURE, wallLOD, cutPlane);
}

/**
 * The default cutPlane will return the WallMass for the WallSection located at the FLOOR_PLAN_CUT_PLANE
 * above the floorElevation that the wall is located on. There may be sections above or below it.
 * In the event that no layer is found, the VoidWall is returned as a fallback.
 */
export function voidWallLayerWithFallback(
  voidWall: VoidWall,
  wallLayer: WallLayerType | undefined,
  wallLOD?: WallLOD,
  cutPlane?: number,
): WallMass {
  const layer = wallMassCrossSectionLayer(voidWall, wallLayer, wallLOD, cutPlane);
  if (!layer) {
    return voidWall;
  }
  return layer;
}

export function wallLayersFromWallMassSystems(
  masses: Table<WallMassSystem>,
  wallLayers: Table<WallLayer>,
): Table<WallLayer> {
  let layers: WallLayer[] = [];
  for (const massId in masses) {
    layers = wallLayersAddFromMasses(layers, masses[massId].walls);
  }
  return layers.reduce(wallLayerTable, { ...wallLayers });
}

export function wallLayersFromWalls(walls: WallTable, wallLayers: Table<WallLayer>): Table<WallLayer> {
  const layers: WallLayer[] = [];
  for (const wallId in walls) {
    const wall = walls[wallId];
    const mass = wall.wallType === WallType.SOLID ? wall.mass : wall;

    for (const layer of mass.layers) {
      addLayers(layers, layer);
    }
  }
  return layers.reduce(wallLayerTable, { ...wallLayers });
}

export function wallLayersAddFromMasses(layers: WallLayer[], masses: WallMassTable): WallLayer[] {
  for (const massId in masses) {
    const mass = masses[massId];
    layers = addLayers(layers, mass as WallLayer);
  }
  return layers;
}

export function wallLayersAddFromRoofs(wallLayers: WallLayer[], roofs: RWTable<GeneratedRoof>): WallLayer[] {
  for (const roofId in roofs) {
    const roof = roofs[roofId];
    const { roofWallSystem } = roof;
    if (!roofWallSystem) {
      continue;
    }
    wallLayers = wallLayersAddFromMasses(wallLayers, roofWallSystem.wallMassSystem.walls);
  }
  return wallLayers;
}

/**
 * Reduce an array of WallLayers into a table
 */
export function wallLayerTable(acc: RWTable<WallLayer>, layer: WallLayer) {
  acc[layer.id] = layer;
  return acc;
}

/**
 * Recursively traverse and flatten the layers of a WallLayer
 */
function addLayers(layers: WallLayer[], layer: WallLayer): WallLayer[] {
  layers.push(layer);
  if (layer.layers) {
    for (const subLayer of layer.layers) {
      layers = addLayers(layers, subLayer);
    }
  }
  return layers;
}
